/** @module csi/table/_SelectionMixin */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dijit/registry",
	"csi",
	"csi/_base/underscore",
	"csi/def/Heading",
	"csi/heading/Renderer",
	"csi/heading/Spec",
	"csi/html",
	"csi/locale",
	"csi/types",
	"csi/widget/CheckBox",
	"csi/widget/RadioButton",
	"mobx",
], function(declare, lang, aspect, registry, csi, _, Heading, Renderer, Spec, html, locale, types, CheckBox, RadioButton, {runInAction}) {

	const CSS_SELECTION = "csi-table-selection-column";
	const CSS_BUTTON = CSS_SELECTION + " " + html.CSS_NO_EXPORT;

	/**
	 * Return selection control in view
	 * @param {HTMLTableRowElement} row
	 * @return {Object}
	 */
	function _getSelectionWidget(row) {
		var td = row.firstChild;

		if (!td.classList.contains(CSS_SELECTION)) {
			return null;
		}

		//Because if it's dndTable, there are two elements in the first column, So, just return the last one
		var domNode = td && td.lastChild;
		return domNode && domNode.getAttribute && registry.byNode(domNode);
	}

	/**
	 * Return all selection checkboxes in view
	 * @param {csi.view.Widget} view
	 * @return {CheckBox[]}
	 */
	function _getSelectionWidgets(view) {
		return csi.transform(view.bodyNode.childNodes, function(row) {
			return _getSelectionWidget(row);
		}) || [];
	}

	/**
	 * Update the value of the selection checkbox based on values of displayed checkboxes; if no results, or any unchecked result, then make unchecked.
	 * @param {csi.view.Widget} view
	 */
	var _updateSelectionCheckbox = _.debounce(function(view) {
		if (!view.isValidView()) {
			return;
		}

		var bChecked = false;
		var nDisabled = 0;
		var nVisible = 0;

		_.some(_getSelectionWidgets(view), function(w) {
			if (view.isRowVisible(view.getRefNode(w.domNode))) {
				++nVisible;

				if (w.initialDisabledValue) {
					++nDisabled;
				} else {
					bChecked = true;

					if (!w.get("value")) {
						bChecked = false;
						return true;
					}
				}
			}

			return false;
		});

		html.setVisible(view.selectionCheckBox, nVisible > nDisabled);
		html.resetWidgetValue(view.selectionCheckBox, bChecked);
	}, 100);

	var _Renderer = declare(Renderer, {
		/**
		 * Render checkbox or radio-button for table selection
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {DOMNode} refNode
		 */
		headingToHtml: function(ctx, h, refNode) {
			var view = ctx.view;
			var tr = refNode.parentNode;
			var qr = view.getQueryResultByDomNode(tr);
			var fd = ctx.fd;

			if (qr.canAggregate()) {
				var bEdit = fd.canSelectNode(ctx);
				var bSelected = fd.isSelection(ctx, true);
				var w;

				// highlighting is not allowed if selection is ON but the row is not allowed for selection
				if (bEdit) {
					view.enableRefNodeHighlighting(tr);
				}

				if (bSelected && bEdit) {
					view.select(tr, true);
				}

				if (fd.m_bSelectOne) {
					if (bSelected && fd.m_bOmitSelections && fd.isAggregated(ctx, true)) {
						bSelected = false;
					}

					w = new RadioButton({
						name: view.getRadioButtonName(),
						value: bSelected,
						disabled: !bEdit
					});
				} else {
					// preselect node if not already selected
					if (!bSelected && bEdit && fd.bPreselectAll) {
						fd.changeSelection(ctx, true, true);
						bSelected = true;
					}

					w = new CheckBox({
						value: bSelected,
						disabled: !bEdit
					});
				}

				w.placeAt(refNode);

				// save initial value of disabled on widget so it will stay disabled when disabling and then enabling view
				w.initialDisabledValue = !bEdit;

				// indicate action to use for selection
				html.setElementAction(w.domNode, view.ACTION_SELECT);
			}
		}
	});

	return declare(null, {
		/** @property {boolean} bDisableQuickSearch - hide quick search for selection tables */
		//bDisableQuickSearch: false,

		/** @property {boolean} bHierarchicalSelection - indicate if selections should propagate to parents and children */
		//bHierarchicalSelection: false,

		/** @property {string} radioButtonName - unique id for radio button group */
		//radioButtonName: null,

		/** @property {CheckBox} selectionCheckBox - checkbox in header to select all results */
		//selectionCheckBox: null,

		/** @property {RadioButton} selectionRadio - radio button in header to clear selection */
		//selectionRadio: null,

		/** @property {Object[]} _selectionHandles - event handles used to keep selectionCheckBox value in sync with displayed results */
		//_selectionHandles: null,

		/**
		 * Change selection widget value on given row
		 * @param {DOMNode} row
		 * @param {boolean} value
		 */
		changeSelectionWidget: function(row, value) {
			var w = _getSelectionWidget(row);
			if (w) {
				w.set("value", value);

				if (value) {
					// check header selection checkbox if all displayed items are now checked
					if (this.selectionCheckBox) {
						_updateSelectionCheckbox(this);
					}
				} else if (this.selectionCheckBox && this.selectionCheckBox.get("value")) {
					// uncheck header selection checkbox now that one items is not checked
					html.resetWidgetValue(this.selectionCheckBox, false);
				} else if (this.selectionRadio) {
					// select header radio button when another item is unselected
					html.resetWidgetValue(this.selectionRadio, true);
				}
			}
		},

		/**
		 * Enable/disable selection widgets in view
		 * @param {boolean} disabled
		 */
		disableSelectionWidgets: function(disabled) {
			_.each(_getSelectionWidgets(this), function(w) {
				w.set("disabled", disabled || w.initialDisabledValue);
			});
		},

		/**
		 * Render checkbox if selecting multiple rows
		 * @param {DOMNode} th
		 */
		generateSelectionHeaderCheckbox: function(th) {
			const _update = () => {
				_updateSelectionCheckbox(this);
			}

			this.selectionCheckBox = new CheckBox({
				title: locale.resource("TableSelectionsCheckbox"),
				value: false,
				onChange: value => this.selectAll(value)
			}).placeAt(th);

			this._selectionHandles = this._selectionHandles || this.own(
				aspect.after(this, "updatePlaceholder", _update),
				aspect.after(this, "insertRows", _update),
				aspect.after(this, "_setHierarchyVisibility", _update),
				aspect.after(this, "_endView", _update)
			);
		},

		/**
		 * Render radio button that allows user to clear selection
		 * @param {Context} ctx
		 * @param {DOMNode} th
		 */
		generateSelectionHeaderRadioButton: function(ctx, th) {
			var view = this;
			var fd = ctx.fd;

			this.selectionRadio = new RadioButton({
				name: this.getRadioButtonName(),
				value: !fd.getSelectedValue().length,
				onChange: function(value) {
					if (value) {
						fd.changeSelection(view.getContext(csi.EMPTY_URL), true);
					}
				}
			}).placeAt(th);
		},

		/**
		 * Return unique id for radio buttons in view
		 * @return {string}
		 */
		getRadioButtonName: function() {
			return this.radioButtonName = this.radioButtonName || _.uniqueId();
		},

		/**
		 * Return Spec used for selection column
		 * @return {Spec}
		 */
		getSelectionSpec: function() {
			// don't reuse selection spec or else spec id could conflict with other specs
			return new Spec({
				css: CSS_BUTTON,
				heading: lang.mixin(new Heading(), {
					classRenderer: _Renderer,
					columnWidth: types.COLUMN_WIDTH_CHECKBOX
				})
			});
		},

		/**
		 * Return true if row has selection widget
			 * @param {DOMNode} row
			 * @return {boolean}
		 */
		isSelectionRow: function(row) {
			return !!_getSelectionWidget(row);
		},

		/**
		 * Toggle all selections in view to match the passed value
		 * @param {boolean} bSelect
		 */
		toggleAllSelections: function(bSelect) {
			const checkboxes = _getSelectionWidgets(this);

			runInAction(() => {
				for (let checkbox of checkboxes) {
					var row = this.getRefNode(checkbox.domNode);

					if (this.isRowVisible(row) && !checkbox.initialDisabledValue && (bSelect != checkbox.get("value"))) {
						this.toggleRefNodeSelection(row, bSelect);
					}
				}
			});
		},

		/**
		 * Add hierarchical behavior to table selection if so configured
			 * @param {DOMNode} refNode
			 * @param {boolean} bSelect
		 */
		toggleRefNodeSelection: function(refNode, bSelect) {
			this.inherited(arguments);

			if (!this.bHierarchicalSelection) {
				return;
			}

			// (de)select all children
			var fd = this.defView.m_formData;
			var children = this.getRowChildren(refNode, true, true);
			var url = this.getRefNodeUrl(refNode);
			var ctx = this.getContext(url);

			_.each(children, function(row) {
				if (this.isSelectionRow(row)) {
					fd.changeSelection(ctx.setNodeURL(this.getRefNodeUrl(row)), bSelect);
				}
			}, this);

			// update parents
			var rowParent = this.getRowParent(refNode);
			var urlParent;

			while (rowParent && this.isSelectionRow(rowParent)) {
				urlParent = this.getRefNodeUrl(rowParent);

				if (bSelect) {
					// if row selected, make sure parent is selected
					fd.changeSelection(ctx.setNodeURL(urlParent), true);
				} else {
					// if row is deselected, then deselect parent if no more of its children are selected
					children = this.getRowChildren(rowParent, true, true);

					var bChildSelected = _.some(children, function(row) {
						return fd.isSelection(ctx.setNodeURL(this.getRefNodeUrl(row)));
					}, this);

					if (!bChildSelected) {
						fd.changeSelection(ctx.setNodeURL(urlParent), false);
					}
				}

				rowParent = this.getRowParent(rowParent);
			}
		}
	});
});
